/** Marketing catalog — keep in sync with Cravbot/src/services/subscription/default-plans.ts */

export type PlanId = "savor" | "feast" | "banquet" | "gala";

export type PlanChannelLimits = {
  freeOrdersIncluded: number;
  payPerOrderAfter: number;
};

export type PlanLimits = {
  online: PlanChannelLimits;
  dineIn: PlanChannelLimits;
};

export type PlanCatalogFeatures = {
  stt: boolean;
  tts: boolean;
  dineIn: boolean;
  customization: boolean;
  whatsapp: boolean;
  ownerApproval: boolean;
  posPunch: boolean;
  emailOwner: boolean;
  emailCustomer: boolean;
  paymentReceipt: boolean;
};

export type SubscriptionPlanCatalogEntry = {
  planId: PlanId;
  name: string;
  tierLabel: string;
  highlight?: boolean;
  badge?: string;
  isPublic?: boolean;
  contactOnly?: boolean;
  limits: PlanLimits;
  features: PlanCatalogFeatures;
};

export const SUBSCRIPTION_PLANS: SubscriptionPlanCatalogEntry[] = [
  {
    planId: "savor",
    name: "Savor",
    tierLabel: "Starter",
    limits: {
      online: { freeOrdersIncluded: 250, payPerOrderAfter: 50 },
      dineIn: { freeOrdersIncluded: 0, payPerOrderAfter: 50 },
    },
    features: {
      stt: false,
      tts: false,
      dineIn: false,
      customization: false,
      whatsapp: false,
      ownerApproval: true,
      posPunch: true,
      emailOwner: true,
      emailCustomer: true,
      paymentReceipt: false,
    },
  },
  {
    planId: "feast",
    name: "Feast",
    tierLabel: "Growth",
    highlight: true,
    badge: "Most Popular",
    limits: {
      online: { freeOrdersIncluded: 500, payPerOrderAfter: 40 },
      dineIn: { freeOrdersIncluded: 250, payPerOrderAfter: 40 },
    },
    features: {
      stt: false,
      tts: false,
      dineIn: true,
      customization: false,
      whatsapp: true,
      ownerApproval: true,
      posPunch: true,
      emailOwner: true,
      emailCustomer: true,
      paymentReceipt: true,
    },
  },
  {
    planId: "banquet",
    name: "Banquet",
    tierLabel: "Premium",
    limits: {
      online: { freeOrdersIncluded: 750, payPerOrderAfter: 30 },
      dineIn: { freeOrdersIncluded: 500, payPerOrderAfter: 30 },
    },
    features: {
      stt: true,
      tts: true,
      dineIn: true,
      customization: true,
      whatsapp: true,
      ownerApproval: true,
      posPunch: true,
      emailOwner: true,
      emailCustomer: true,
      paymentReceipt: true,
    },
  },
  {
    planId: "gala",
    name: "Gala",
    tierLabel: "Enterprise",
    isPublic: true,
    contactOnly: true,
    limits: {
      online: { freeOrdersIncluded: 0, payPerOrderAfter: 0 },
      dineIn: { freeOrdersIncluded: 0, payPerOrderAfter: 0 },
    },
    features: {
      stt: true,
      tts: true,
      dineIn: true,
      customization: true,
      whatsapp: true,
      ownerApproval: true,
      posPunch: true,
      emailOwner: true,
      emailCustomer: true,
      paymentReceipt: true,
    },
  },
];

/** Self-serve and fixed-tier plans shown with list pricing. */
export const PUBLIC_PRICING_PLANS = SUBSCRIPTION_PLANS.filter((plan) => !plan.contactOnly);

export const GALA_PLAN = SUBSCRIPTION_PLANS.find((plan) => plan.planId === "gala")!;

export const PLAN_FEATURE_LABELS: Record<string, string> = {
  apiAccess: "Ordering API Access",
  aiBot: "AI Ordering Bot",
  dashboard: "Owner Dashboard",
  menuMgmt: "Menu Management",
  posPunch: "POS Integration",
  ownerApproval: "Order Approval Flow",
  emailOwner: "Owner Email Notifications",
  emailCustomer: "Customer Email Notifications",
  whatsapp: "WhatsApp Owner Alerts",
  dineIn: "Dine-in Table Ordering",
  paymentReceipt: "Prepaid Payment Receipts",
  customization: "Dine-in Branding & Voice Settings",
  stt: "Voice Input (STT)",
  tts: "Voice Replies (TTS)",
};

export function maxOnlineOrdersIncluded(): number {
  return Math.max(...PUBLIC_PRICING_PLANS.map((p) => p.limits.online.freeOrdersIncluded));
}

export function formatContactPlanOption(plan: SubscriptionPlanCatalogEntry): string {
  if (plan.contactOnly) {
    return `${plan.name} — ${plan.tierLabel} (custom features & limits)`;
  }
  const { online, dineIn } = plan.limits;
  if (dineIn.freeOrdersIncluded > 0) {
    return `${plan.name} — ${plan.tierLabel} (${online.freeOrdersIncluded.toLocaleString()} online + ${dineIn.freeOrdersIncluded.toLocaleString()} dine-in/mo)`;
  }
  return `${plan.name} — ${plan.tierLabel} (${online.freeOrdersIncluded.toLocaleString()} online orders/mo)`;
}

export function landingPlanBullets(plan: SubscriptionPlanCatalogEntry): string[] {
  const { online, dineIn } = plan.limits;
  const orderLine =
    dineIn.freeOrdersIncluded > 0
      ? `${online.freeOrdersIncluded.toLocaleString()} online + ${dineIn.freeOrdersIncluded.toLocaleString()} dine-in orders/month`
      : `${online.freeOrdersIncluded.toLocaleString()} online orders/month`;

  switch (plan.planId) {
    case "savor":
      return [
        orderLine,
        "Ordering API access",
        "Owner dashboard & menu management",
        "POS integration",
        "Order approval & email alerts",
      ];
    case "feast":
      return [
        orderLine,
        "Everything in Savor",
        "Dine-in table ordering (QR)",
        "WhatsApp owner alerts",
        "Prepaid payment receipt upload",
        "Email notifications",
      ];
    case "banquet":
      return [
        orderLine,
        "Everything in Feast",
        "Voice ordering (STT/TTS)",
        "Dine-in branding & customization",
        "Prepaid payment receipt upload",
        "WhatsApp owner alerts",
      ];
    case "gala":
      return [
        "Tailored online & dine-in limits",
        "Pick only the features you need",
        "API access always included",
        "Custom STT/TTS, POS, and alerts",
        "Dedicated onboarding & support",
      ];
    default:
      return [orderLine];
  }
}

export type PlanFeatureValue = boolean | string;

export function buildPlanFeatureMatrix(
  features: PlanCatalogFeatures,
): Record<string, PlanFeatureValue> {
  return {
    apiAccess: true,
    aiBot: true,
    dashboard: true,
    menuMgmt: true,
    posPunch: features.posPunch,
    ownerApproval: features.ownerApproval,
    emailOwner: features.emailOwner,
    emailCustomer: features.emailCustomer,
    whatsapp: features.whatsapp,
    dineIn: features.dineIn,
    paymentReceipt: features.paymentReceipt,
    customization: features.customization,
    stt: features.stt,
    tts: features.tts,
  };
}

export function formatDineInLimit(plan: SubscriptionPlanCatalogEntry): string {
  if (plan.limits.dineIn.freeOrdersIncluded === 0) {
    return "Not included";
  }
  return plan.limits.dineIn.freeOrdersIncluded.toLocaleString();
}
